<script setup lang="ts">
import { DtwinSelectionQuickFilter } from "../models/DtwinSelectionQuickFilter.enum";
import { type SelectionModeEnum } from "@lxc/app-device-common";
import type LxcTable from "@lxc/app-device-common/src/components/LxcTable.vue";
import { storeToRefs } from "pinia";
import type { ComputedRef, FunctionalComponent, Ref, SVGAttributes } from "vue";
import { useAcl } from "vue-simple-acl";
import type { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  type FilterOptions,
  Filters,
  type FilterSelectionValue,
  type Option,
} from "~/core/models/filters";
import filtersUtils from "~/core/utils/filters.utils";
import { CAMPAIGN_PARAMS_SCOPE } from "~/modules/campaign/constants/constants";
import {
  type DeviceTypeFilter,
  useDeviceTypeFilters,
} from "~/modules/dtwin/composables/useDeviceTypeFilters";
import { useFetchDtwinModelUIConfigs } from "~/modules/dtwin/composables/useFetchDtwinModelUIConfigs";
import { useFetchDtwinModels } from "~/modules/dtwin/composables/useFetchDtwinModels";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import { PATH_DTWINS_ID } from "~/modules/dtwin/constants";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinColumns } from "~/modules/dtwin/models/DtwinColumns.enum";
import { DtwinCompatibilityQuickFilter } from "~/modules/dtwin/models/DtwinCompatibilityQuickFilter.enum";
import type { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import { DtwinListDisplayContext } from "~/modules/dtwin/models/DtwinListDisplayContext.enum";
import type { DtwinModelUiConfig } from "~/modules/dtwin/models/DtwinModelUiConfig.interface";
import type { DynamicUI } from "~/modules/dtwin/models/DynamicUI.interface";
import type { QuickFilterGroup } from "~/modules/dtwin/models/QuickFilterGroup.interface";
import type { QuickFilterItem } from "~/modules/dtwin/models/QuickFilterItem.interface";
import { getMergedOrGenericUIConfig } from "~/modules/dtwin/utils/getMergedOrGenericUIConfig";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import type { ColumnNameAndWidthI } from "~/types/table.type";
import {
  calculateTableMinWidth,
  getColumnWidth,
  parseColumnWidth,
} from "~/utils/table.utils";
import ILxcAlertTriangle from "~icons/lxc/alert-triangle";
import ILxcCheckCircle from "~icons/lxc/check-circle";

interface DeviceCompatibilityQuickFilterConfigI {
  filterType: DtwinCompatibilityQuickFilter;
  icon?: FunctionalComponent<SVGAttributes>;
}

const props = defineProps<{
  reload?: boolean;
  tableOnly?: boolean;
  fleetUid?: string;
  columns?: DtwinColumns[];
  defaultFilters?: Map<Filters, FilterSelectionValue>;
  searchMode?: SearchMode;
  searchBarClass?: string | string[];
  noQuickActionMenu?: boolean;
  noQuickFilters?: boolean;
  context?: DtwinListDisplayContext;
  isRemovingFromFleet?: boolean;
  notInFleetUid?: string;
  selectedDtwins?: Dtwin[];
  useQueryParametersForPagination?: boolean;
  rowClickSelectionMode?: SelectionModeEnum;
  displayDeviceCompatibilityQuickFilter?: boolean;
  compatibilityCriteria?: string;
  noRedirection?: boolean;
  clickable?: boolean;
  disableUnselect?: boolean;
  showQuickFilterSelectedDtwins?: boolean;
  withinCampaignScope?: boolean;
}>();

const emit = defineEmits([
  "update:reload",
  "update:selected-dtwins",
  "removeFromFleet",
  "rowClick",
]);

const { t } = useI18n();

const router = useRouter();

const acl = useAcl();

const initialSelectedDtwins = ref<Dtwin[]>(props.selectedDtwins ?? []);

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  isLoading,
  results,
  error,
  setFilter,
  fetchData,
  search,
  onSearch,
  searchOnSectors,
  filters,
  onSortChange,
} = useFetchDtwins({
  organizationCode: currentOrganization.value?.code,
  searchMode: props.searchMode,
  useQueryParametersForPagination: props.useQueryParametersForPagination,
});

const {
  results: resultsAllDtwinModelUIConfigs,
  fetchAll: fetchAllDtwinModelUIConfigs,
} = useFetchDtwinModelUIConfigs({
  organizationCode: currentOrganization.value?.code,
});

const {
  isLoading: isLoadingAllDtwinModels,
  error: errorAllDtwinModels,
  results: resultsAllDtwinModels,
  fetchAll: fetchAllDtwinModels,
} = useFetchDtwinModels({ organizationCode: currentOrganization.value?.code });

const { selectedSectors } = storeToRefs(useSectorStore());

const deviceModelsFilters: Ref<DeviceTypeFilter[]> = ref([]);

const isAllowedToManageFleet = computed(() =>
  acl.can(ACLRoles.CAN_WRITE_DEVICE),
);

const canDeleteDtwins = computed(() => acl.can(ACLRoles.CAN_WRITE_DEVICE));

const showActionInProgress: ComputedRef<boolean | undefined> = computed(() =>
  props.columns?.includes(DtwinColumns.ACTION_IN_PROGRESS),
);
const actionInProgressLabel: Ref<string> = ref(
  t("dtwins.list.attributes.actionInProgress"),
);
const showDtwinFilters: ComputedRef<boolean> = computed(() => {
  return !props.tableOnly && !props.noQuickFilters;
});

const resultUIConfig: Ref<DynamicUI | undefined> = ref();

const lifecycleStatesOptions: ComputedRef<FilterOptions> = computed(() => {
  let lifecycles: Option[] = [];
  if (results.value?.distinctValues?.lifeCycleState) {
    lifecycles = results.value?.distinctValues.lifeCycleState?.map(
      (state: DtwinLifeCycleState) => {
        return {
          label: t(`dtwins.lifeCycleState.${state}`),
          value: state,
        };
      },
    );
  }
  return {
    label: t("dtwins.filters.lifecycleState"),
    options: lifecycles,
  };
});

function applyDefaultFilters() {
  if (props.defaultFilters) {
    for (const [filter, value] of props.defaultFilters) {
      setFilter(filter, value || "");
    }
  }
}

applyDefaultFilters();

const tableRef = ref<InstanceType<typeof LxcTable>>();

const searchQuery = computed({
  get() {
    return (filters.get(Filters.DTWIN_NAME_OR_SERIAL_NUMBER) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.DTWIN_NAME_OR_SERIAL_NUMBER, searchQuery);
  },
});

async function reloadData(page?: number, pageSize?: number) {
  const params = new Map();
  if (
    selectedCompatibilityFilter.value ===
    DtwinCompatibilityQuickFilter.COMPATIBLE_DEVICES
  ) {
    params.set("compatibilityCriteria", props.compatibilityCriteria);
  } else if (
    props.compatibilityCriteria &&
    selectedCompatibilityFilter.value ===
      DtwinCompatibilityQuickFilter.INCOMPATIBLE_DEVICES
  ) {
    params.set(
      "compatibilityCriteria",
      filtersUtils.negateRSQLExpression(props.compatibilityCriteria),
    );
  }
  params.set("showActionInProgress", showActionInProgress.value);

  if (showDtwinFilters.value) {
    params.set("distinctValues", "lifeCycleState");
  }
  params.set(CAMPAIGN_PARAMS_SCOPE, props.withinCampaignScope);

  if (props.showQuickFilterSelectedDtwins) {
    const selections = initialSelectedDtwins.value?.map((dtwin) => dtwin.uid);
    if (
      selections.length > 0 &&
      selectedDevicesSelectionFilter.value ===
        DtwinSelectionQuickFilter.SELECTED_DEVICES
    ) {
      setFilter(Filters.DTWIN_UID, selections);
      setFilter(Filters.DTWIN_UID_OUT, []);
    } else if (
      selectedDevicesSelectionFilter.value ===
      DtwinSelectionQuickFilter.UNSELECTED_DEVICES
    ) {
      setFilter(Filters.DTWIN_UID, []);
      setFilter(Filters.DTWIN_UID_OUT, selections);
    } else if (
      selectedDevicesSelectionFilter.value ===
      DtwinSelectionQuickFilter.ALL_DEVICES
    ) {
      setFilter(Filters.DTWIN_UID, []);
      setFilter(Filters.DTWIN_UID_OUT, []);
    }
  }

  await fetchData(page, pageSize, params);

  // If no selected devices, the filter won't be applied
  if (
    props.showQuickFilterSelectedDtwins &&
    initialSelectedDtwins.value?.length === 0 &&
    results.value &&
    selectedDevicesSelectionFilter.value ===
      DtwinSelectionQuickFilter.SELECTED_DEVICES
  ) {
    results.value.results = [];
  }

  setSelection();
  initialSelectedDtwins.value = props.selectedDtwins ?? [];
  fetchUIConfigForMostRecentModel();
}

function fetchUIConfigForMostRecentModel() {
  const modelUids: string[] | undefined = toRaw(
    selectedDeviceModelFilter.value?.value,
  ) as string[] | undefined;
  if (modelUids && modelUids.length > 1) {
    modelUids.sort((a: string, b: string) => {
      const aModel = resultsAllDtwinModelUIConfigs.value?.find(
        (model) => model.deviceModelUid === a,
      );
      const bModel = resultsAllDtwinModelUIConfigs.value?.find(
        (model) => model.deviceModelUid === b,
      );
      if (aModel?.modifiedAt && bModel?.modifiedAt) {
        return (
          new Date(bModel.modifiedAt).getTime() -
          new Date(aModel.modifiedAt).getTime()
        );
      } else if (aModel) {
        return -1;
      } else {
        return 0;
      }
    });
  }
  let mostRecentModel;
  if (modelUids && modelUids?.length > 0) {
    mostRecentModel = resultsAllDtwinModelUIConfigs.value?.find((uiConfig) => {
      return uiConfig.deviceModelUid === modelUids[0];
    });
  }
  fetchUIConfig(mostRecentModel);
}

/**
 * Select Dtwins
 */
const setSelection = () => {
  if (props.selectedDtwins && tableRef.value && tableRef.value.data) {
    tableRef.value.toggleTableSelectionMode(props.selectedDtwins.length > 0);
    for (const row of tableRef.value.data as Dtwin[]) {
      tableRef.value.toggleRowSelection(
        row,
        props.selectedDtwins?.some(
          (selectedDevice) => selectedDevice.uid === row.uid,
        ),
      );
    }
  }
};

async function executeSearch(replaceHistory?: boolean) {
  if (props.fleetUid) {
    setFilter(Filters.DTWINS_IN_FLEET, props.fleetUid);
  }
  if (props.notInFleetUid) {
    setFilter(Filters.DTWINS_NOT_IN_FLEET, props.notInFleetUid);
  }

  await searchOnSectors(selectedSectors.value, replaceHistory);
}

const selectedDeviceModelFilter = computed(() => {
  return filters.get(Filters.DTWIN_MODEL_TYPE);
});

const quickFiltersWrapperClass = computed(() => {
  const wrapperClass = ["flex", "flex-wrap", "gap-2"];

  if (props.selectedDtwins && props.selectedDtwins.length > 0) {
    wrapperClass.push("mb-2");
  } else if (props.displayDeviceCompatibilityQuickFilter) {
    wrapperClass.push("mb-[1.125rem]", "!gap-4");
  } else {
    wrapperClass.push("mb-6");
  }

  return wrapperClass;
});

async function getDevicesByModel(modelUids?: string[]) {
  if (modelUids) {
    // if unselect device models
    if (
      Array.isArray(selectedDeviceModelFilter?.value?.value) &&
      selectedDeviceModelFilter?.value?.value.length === modelUids.length &&
      (selectedDeviceModelFilter?.value?.value as string[]).every((uid) =>
        modelUids?.includes(uid),
      )
    ) {
      setFilter(Filters.DTWIN_MODEL_TYPE, []);
      fetchUIConfig();
    } else {
      setFilter(Filters.DTWIN_MODEL_TYPE, modelUids);
    }
    await executeSearch();
  }
}

const selectedCompatibilityFilter: Ref<DtwinCompatibilityQuickFilter> = ref(
  DtwinCompatibilityQuickFilter.ALL_DEVICES,
);

async function filterByCompatibility(
  compatibilityFilter: DtwinCompatibilityQuickFilter,
) {
  selectedCompatibilityFilter.value = compatibilityFilter;
  await reloadData();
}

const selectedDevicesSelectionFilter: Ref<DtwinSelectionQuickFilter> = ref(
  DtwinSelectionQuickFilter.ALL_DEVICES,
);

async function filterBySelection(selectionFilter: DtwinSelectionQuickFilter) {
  initialSelectedDtwins.value = props.selectedDtwins ?? [];
  selectedDevicesSelectionFilter.value = selectionFilter;
  await reloadData();
}

const getCompatibilityFilterIconClass = (
  compatibilityFilter: DtwinCompatibilityQuickFilter,
) => {
  if (
    compatibilityFilter !== selectedCompatibilityFilter.value &&
    compatibilityFilter === DtwinCompatibilityQuickFilter.COMPATIBLE_DEVICES
  ) {
    return "text-success-700";
  } else if (
    compatibilityFilter !== selectedCompatibilityFilter.value &&
    compatibilityFilter === DtwinCompatibilityQuickFilter.INCOMPATIBLE_DEVICES
  ) {
    return "text-error-700";
  } else {
    return "text-inherit";
  }
};

const deviceCompatibilityQuickFilters: DeviceCompatibilityQuickFilterConfigI[] =
  [
    {
      filterType: DtwinCompatibilityQuickFilter.ALL_DEVICES,
    },
    {
      filterType: DtwinCompatibilityQuickFilter.COMPATIBLE_DEVICES,
      icon: ILxcCheckCircle,
    },
    {
      filterType: DtwinCompatibilityQuickFilter.INCOMPATIBLE_DEVICES,
      icon: ILxcAlertTriangle,
    },
  ];

const quickFilterGroups = computed(() => {
  const groups: QuickFilterGroup[] = [];

  // Group 1: Device Models
  if (deviceModelsFilters.value.length > 0 && !props.noQuickFilters) {
    groups.push({
      key: "deviceModels",
      items: deviceModelsFilters.value.map((model) => ({
        key: model.value,
        label: model.label,
        uids: model.uids ? [...model.uids] : [],
        isActive: (
          filters.get(Filters.DTWIN_MODEL_TYPE)?.value as string[]
        )?.some((uid) => model.uids?.includes(uid)),
      })),
      handler: (item: QuickFilterItem) => getDevicesByModel(item.uids),
    });
  }

  // Group 2: Compatibility
  if (props.displayDeviceCompatibilityQuickFilter) {
    groups.push({
      key: "compatibility",
      items: deviceCompatibilityQuickFilters.map((filterConfig) => ({
        key: filterConfig.filterType,
        label: t(
          `dtwins.filters.compatibilityQuickFilters.${filterConfig.filterType}`,
        ),
        icon: filterConfig.icon,
        isActive: selectedCompatibilityFilter.value === filterConfig.filterType,
        iconClass: getCompatibilityFilterIconClass(filterConfig.filterType),
      })),
      handler: (item) =>
        filterByCompatibility(item.key as DtwinCompatibilityQuickFilter),
    });
  }

  // Group 3: Selection
  if (props.showQuickFilterSelectedDtwins) {
    const selectionFilters = [
      DtwinSelectionQuickFilter.ALL_DEVICES,
      DtwinSelectionQuickFilter.SELECTED_DEVICES,
      DtwinSelectionQuickFilter.UNSELECTED_DEVICES,
    ];
    groups.push({
      key: "selection",
      items: selectionFilters.map((filter) => ({
        key: filter,
        label: t(`dtwins.filters.selectionQuickFilters.${filter}`),
        isActive: selectedDevicesSelectionFilter.value === filter,
      })),
      handler: (item) =>
        filterBySelection(item.key as DtwinSelectionQuickFilter),
    });
  }

  return groups;
});

function handleFilterClick({
  group,
  item,
}: {
  group: QuickFilterGroup;
  item: QuickFilterItem;
}) {
  // The 'handler' function is still defined in the computed property
  // This keeps the logic for what to do with the click in the parent.
  if (group.handler) {
    group.handler(item);
  }
}

const handleSelect = (selectedDevices: Dtwin[]) => {
  emit(
    "update:selected-dtwins",
    props.selectedDtwins
      ?.filter(
        (selectedDevice: Dtwin) =>
          !tableRef.value ||
          !(tableRef.value.data as Dtwin[])?.find(
            (row: Dtwin) => row.uid === selectedDevice.uid,
          ),
      )
      .filter(
        (selectedDevice: Dtwin) =>
          !selectedDevices.find((row) => row.uid === selectedDevice.uid),
      )
      .concat(selectedDevices) ?? selectedDevices,
  );
};

const onCancelSelection = () => {
  emit("update:selected-dtwins", []);
  if (tableRef.value) {
    tableRef.value.selectedRows = [];
  }
};

const onRowClick = (dtwin: Dtwin) => {
  emit("rowClick", dtwin);
  if (!props.noRedirection) {
    router.push({ name: PATH_DTWINS_ID, params: { id: dtwin.uid } });
  }
};

/**
 * Build a columns width list from the UI config columns min width or width
 * Example:
 * [{
 *   columnName: "attributes.friendlyName",
 *   width: 10
 * }, ...
 * ]
 */
const columnsWidth: ComputedRef<ColumnNameAndWidthI<DtwinColumns>[]> = computed(
  () => {
    const columnsNameAndWidth: ColumnNameAndWidthI<DtwinColumns>[] = [];

    if (resultUIConfig.value?.list?.columns) {
      for (let i = 0; i < resultUIConfig.value.list.columns.length; i++) {
        const dynamicUIColumn = resultUIConfig.value.list.columns[i];
        const widthRem = dynamicUIColumn.minWidth || dynamicUIColumn.width;
        if (widthRem !== undefined) {
          const width = parseColumnWidth(widthRem);
          if (width !== undefined) {
            columnsNameAndWidth.push({
              columnName: dynamicUIColumn.id as DtwinColumns,
              width,
            });
          }
        }
      }
    }

    return columnsNameAndWidth.concat([
      {
        columnName: DtwinColumns.ACTION,
        width: 6,
      },
      {
        columnName: DtwinColumns.ACTION_IN_PROGRESS,
        width: 12,
      },
    ]);
  },
);

const tableMinWidth: ComputedRef<string | undefined> = computed(() => {
  return calculateTableMinWidth<DtwinColumns>(
    columnsWidth.value,
    props.columns,
  );
});

const getWidth = (columnName: DtwinColumns): string | undefined => {
  return getColumnWidth<DtwinColumns>(columnName, columnsWidth.value);
};

function isRowDisabled(row: Dtwin): boolean {
  return (
    props.disableUnselect &&
    !!initialSelectedDtwins.value?.find((value: Dtwin) => {
      return value.uid === row.uid;
    })
  );
}

function onRemoveFromFleet(dtwins: Dtwin[]) {
  emit("removeFromFleet", dtwins);
}

const onDeleteDtwins = () => {
  reloadData();
  onCancelSelection();
};

const deviceTypeOptions = computed(() => {
  if (deviceModelsFilters.value.length > 0) {
    return {
      label: t("dtwins.filters.deviceType"),
      options: deviceModelsFilters.value.map((model) => ({
        value: model.uids || [],
        label: model.label,
      })),
    };
  }
  return undefined;
});

function fetchUIConfig(modelUIConfig?: DtwinModelUiConfig) {
  resultUIConfig.value = getMergedOrGenericUIConfig(modelUIConfig?.properties);
}

watch(() => props.selectedDtwins, setSelection);

/**
 * Reload data if global sector filter has been applied
 */
watch(selectedSectors, () => executeSearch(true));

watch(
  () => props.fleetUid,
  () => executeSearch(),
);

watch(
  () => props.defaultFilters,
  () => {
    applyDefaultFilters();
    reloadData();
  },
);

watch(
  () => props.reload,
  (reload) => {
    if (reload) {
      reloadData();
      emit("update:reload", false);
    }
  },
);

onMounted(async () => {
  await fetchAllDtwinModels();
  await fetchAllDtwinModelUIConfigs();
  fetchUIConfig();
  const { deviceTypeFilters } = await useDeviceTypeFilters();
  deviceModelsFilters.value = deviceTypeFilters.value;
  await onSearch(reloadData, false);
  await executeSearch(true);
});

defineExpose({
  onCancelSelection,
});
</script>

<template>
  <div v-if="!tableOnly" :class="`mb-6 ${searchBarClass}`">
    <search-bar
      v-model:search-query="searchQuery"
      :search-placeholder="t('dtwins.filters.searchByNameOrSerialNumber')"
      @clear="search"
      @search="search"
    />
  </div>

  <container-component
    :is-loading="
      (!resultsAllDtwinModels && !errorAllDtwinModels) || (!results && !error)
    "
    :px="0"
    :py="0"
  >
    <div class="flex justify-between">
      <!-- Avoid ugly display of the quick filters before the list has been displayed. The quick filter arrives with the list the first time the list is loaded -->
      <div
        v-if="
          !tableOnly &&
          (resultsAllDtwinModels || errorAllDtwinModels) &&
          (results || error)
        "
      >
        <dtwin-list-quick-filters
          :groups="quickFilterGroups"
          :class="quickFiltersWrapperClass"
          @filter-click="handleFilterClick"
        />
      </div>
      <dtwin-filters
        v-if="showDtwinFilters"
        :organization-code="currentOrganization?.code"
        :filters="filters"
        :device-type-options="deviceTypeOptions"
        :lifecycle-states-options="lifecycleStatesOptions"
        @change="setFilter"
        @enter="search"
      />
    </div>

    <dtwin-list-quick-actions
      v-if="selectedDtwins && selectedDtwins.length > 0 && !noQuickActionMenu"
      :context="context"
      :is-removing-from-fleet="isRemovingFromFleet"
      :not-in-fleet-uid="notInFleetUid"
      :selected-dtwins="selectedDtwins"
      :can-delete-dtwins="canDeleteDtwins"
      :organization-code="currentOrganization?.code"
      @cancel-selection="onCancelSelection"
      @remove-from-fleet="onRemoveFromFleet"
      @delete-dtwins="onDeleteDtwins"
    />
    <lxc-table
      ref="tableRef"
      :min-width="tableMinWidth"
      :context="results?.pagination"
      :data="results?.results"
      :is-loading="isLoadingAllDtwinModels || isLoading"
      :error="errorAllDtwinModels?.toError() || error?.toError()"
      :empty-text="t('dtwins.empty')"
      :clickable="clickable"
      :row-click-selection-mode="rowClickSelectionMode"
      :is-row-disabled="isRowDisabled"
      @change-page-and-page-size="reloadData"
      @row-click="onRowClick"
      @select="handleSelect"
      @select-all="handleSelect"
      @sort-change="onSortChange"
    >
      <lxc-table-column v-if="selectedDtwins" type="selection" />
      <lxc-table-column
        v-for="column in resultUIConfig?.list?.columns"
        :key="column.id"
        :prop="column.id"
        :label="column.label"
        :sort-mode="column.sortMode"
        :width="column.width"
        :min-width="column.minWidth"
        :class="column.noVerticalPadding ? '!py-0' : ''"
      >
        <template v-if="column.formattingProcess" #default="scope">
          <dtwin-formatting-process :dynamic-ui="column" :dtwin="scope.row" />
        </template>
      </lxc-table-column>
      <lxc-table-column
        v-if="columns?.includes(DtwinColumns.ACTION_IN_PROGRESS)"
        :label="actionInProgressLabel"
        :width="getWidth(DtwinColumns.ACTION_IN_PROGRESS)"
      >
        <template #default="scope">
          <div
            v-if="scope.row.actionInProgress"
            class="rounded-full bg-primary-100 size-6 flex items-center justify-center"
            :title="actionInProgressLabel"
          >
            <i-lxc-check class="text-primary-600" width="12" height="12" />
          </div>
        </template>
      </lxc-table-column>
      <lxc-table-column
        v-if="
          (columns && columns.includes(DtwinColumns.ACTION)) ||
          context === DtwinListDisplayContext.DTWINS
        "
        class="!py-0"
        :width="getWidth(DtwinColumns.ACTION)"
      >
        <template #default="scope">
          <div class="flex gap-4">
            <dtwin-actions
              :dtwin="scope.row"
              :dtwin-model="
                resultsAllDtwinModels?.find(
                  (dtwinModel) => dtwinModel.uid === scope.row.deviceModelUid,
                )
              "
              :context="context"
              :is-allowed-to-manage-fleet="isAllowedToManageFleet"
              :is-removing-from-fleet="isRemovingFromFleet"
              @remove-from-fleet="$emit('removeFromFleet', [scope.row])"
              @life-cycle-state-updated="reloadData"
            />
            <dtwin-action-delete
              v-if="
                context === DtwinListDisplayContext.DTWINS && canDeleteDtwins
              "
              :dtwin-uids="scope.row.uid"
              :organization-code="currentOrganization?.code"
              :button-class="['invisible']"
              @delete="reloadData"
            />
          </div>
        </template>
      </lxc-table-column>
    </lxc-table>
  </container-component>
</template>

<style lang="scss" scoped>
:deep(button) {
  span {
    margin-left: 0 !important;
  }
}

:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;

          svg {
            height: 20px;
            width: 20px;
          }
        }
      }
    }
  }
}
</style>
